import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { Participant } from 'livekit-client'
import { Track } from 'livekit-client'

import { ClickEvent, Menu, MenuItem } from '@szhsin/react-menu'
import { TrackSource } from '@livekit/protocol'

import {
  MicIcon,
  MicDisabledIcon,
  CameraIcon,
  CameraDisabledIcon,
  MoreIcon,
  DeleteIcon,
  ScreenShareIcon,
  ScreenShareStopIcon,
  VoiceOn,
  VoiceOff,
} from '@/src/assets/icons'

import { useMaybeRoomContext } from '@/src/context'
import { useIsMuted } from '@/src/hooks'

import { useAppSelector } from '@/src/store/hooks'
import { selectRoomState } from '@/src/store/slices/room'
import { selectBranding, selectIsAdmin } from '@/src/store/slices/meetData'
import { selectParticipantsReactions } from '@/src/store/slices/reactions'
import { selectAccessToken } from '@/src/store/slices/token'

// Набор цветов для аватарок участников
const AVATAR_COLORS = [
  ['#6366f1', '#8b5cf6'], // indigo-violet
  ['#ec4899', '#f43f5e'], // pink-rose
  ['#14b8a6', '#06b6d4'], // teal-cyan
  ['#f97316', '#eab308'], // orange-yellow
  ['#22c55e', '#10b981'], // green-emerald
  ['#3b82f6', '#6366f1'], // blue-indigo
  ['#a855f7', '#d946ef'], // purple-fuchsia
  ['#ef4444', '#f97316'], // red-orange
  ['#0ea5e9', '#14b8a6'], // sky-teal
  ['#84cc16', '#22c55e'], // lime-green
]

// Цвет для админов/организаторов (золотой)
const ADMIN_AVATAR_COLORS = ['#f59e0b', '#d97706'] // amber-gold

// Генерация стабильного индекса цвета на основе строки
function getColorIndex(str: string): number {
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash)
  }
  return Math.abs(hash) % AVATAR_COLORS.length
}
/** @public */
export interface ParticipantListItemProps {
  participant: Participant
  groupOptions?: { id: string | null; name: string }[]
}

export function ParticipantListItem({
  participant,
  groupOptions = [],
  ...props
}: ParticipantListItemProps) {
  const { t } = useTranslation()
  const { livekit_url } = useAppSelector(selectRoomState)
  const { text_primary_color, primary_color } = useAppSelector(selectBranding)
  const isAdmin = useAppSelector(selectIsAdmin)
  const participantsReactions = useAppSelector(selectParticipantsReactions)
  const accessToken = useAppSelector(selectAccessToken)

  const micTrack = participant.getTrackPublication(Track.Source.Microphone)
  const micRef = useMemo(
    () => ({
      participant: participant,
      source: Track.Source.Microphone,
      publication: micTrack,
    }),
    [micTrack, participant],
  )

  const canPublishSources = participant.permissions?.canPublishSources
  const canPublishMicrophone =
    participant.permissions?.canPublish &&
    (!canPublishSources?.length || canPublishSources.includes(TrackSource.MICROPHONE))
  const canPublishScreenShare =
    participant.permissions?.canPublish &&
    (!canPublishSources?.length || canPublishSources.includes(TrackSource.SCREEN_SHARE))

  const cameraTrack = participant.getTrackPublication(Track.Source.Camera)

  const cameraRef = useMemo(
    () => ({
      participant: participant,
      source: Track.Source.Camera,
      publication: cameraTrack,
    }),
    [cameraTrack, participant],
  )

  const isVideoMuted = useIsMuted(cameraRef)
  const isAudioMuted = useIsMuted(micRef)

  const roomContext = useMaybeRoomContext()

  // Состояние загрузки при перемещении участника между группами
  const [isUpdatingGroup, setIsUpdatingGroup] = useState(false)

  // Нормализуем опции групп (защита от undefined)
  const normalizedGroupOptions = groupOptions ?? []

  // Проверяем, является ли локальный участник куратором
  const localParticipantIsCurator =
    roomContext?.localParticipant?.attributes?.isCurator === 'true'

  // Определяем, может ли текущий пользователь управлять группами участников
  const canManageParticipantGroups = !participant.isLocal && (isAdmin || localParticipantIsCurator)

  // Получаем текущую группу участника
  const currentGroupId = useMemo(() => {
    try {
      if (roomContext?.metadata) {
        const metadata = JSON.parse(roomContext.metadata)
        const participantGroupMap = metadata.participantGroupMap || {}

        if (participant.identity in participantGroupMap) {
          return participantGroupMap[participant.identity]
        }
      }
    } catch (e) {
      console.error('Error parsing currentGroupId from metadata:', e)
    }

    return participant.attributes?.groupId ?? null
  }, [roomContext?.metadata, participant.identity, participant.attributes?.groupId])

  // Проверяем, является ли участник админом/организатором
  const isParticipantAdmin = participant.permissions?.canPublish &&
    participant.permissions?.canSubscribe &&
    participant.permissions?.canPublishData &&
    (participant.attributes?.isAdmin === 'true' || participant.attributes?.isCurator === 'true')

  // Генерируем стабильный цвет аватарки на основе имени/identity участника
  // Админы получают особый золотой цвет
  const avatarColors = useMemo(() => {
    if (isParticipantAdmin) {
      return ADMIN_AVATAR_COLORS
    }
    const key = participant.name || participant.identity || 'default'
    const colorIndex = getColorIndex(key)
    return AVATAR_COLORS[colorIndex]
  }, [participant.name, participant.identity, isParticipantAdmin])

  // Проверяем, есть ли группы, в которые можно переместить участника
  // (исключаем текущую группу из списка доступных)
  const hasGroupTargets =
    canManageParticipantGroups &&
    normalizedGroupOptions.some((option) => (option.id ?? null) !== currentGroupId)

  // Определяем, может ли пользователь удалять участников (только админы)
  const canRemoveParticipant = !participant.isLocal && isAdmin

  // Показываем меню, если есть действия, которые можно выполнить
  const shouldShowMenu = !participant.isLocal && (canRemoveParticipant || hasGroupTargets)

  const togglePermissions = useCallback(
    async (source: Track.Source) => {
      const track = participant.getTrackPublication(source)

      if (!track || !livekit_url) return

      const payload = {
        room: roomContext.name,
        identity: participant.identity,
        trackSid: track.trackSid,
        muted: true,
      }

      await fetch(`${window.origin}/api/mute_publish_track?livekit_url=${livekit_url}`, {
        method: 'POST',
        body: JSON.stringify(payload),
        headers: {
          authorization: accessToken,
        },
      }).then((res) => {
        console.log('Log during mute publish track: \n', res)
      })
    },
    [participant, roomContext?.name, livekit_url],
  )

  const removeFromRoom = useCallback(
    async (event: ClickEvent) => {
      event.stopPropagation = true

      const payload = {
        room: roomContext.name,
        identity: participant.identity,
        token: accessToken,
        localParticipant: roomContext.localParticipant,
      }

      if (!livekit_url) return

      await fetch(`${window.origin}/api/remove_participant?livekit_url=${livekit_url}`, {
        method: 'POST',
        body: JSON.stringify(payload),
      }).then((res) => {
        console.log('Error during remove participant: \n', res)
      })
    },
    [participant?.identity, roomContext?.localParticipant, roomContext?.name, livekit_url],
  )

  const toggleMicrophoneBlock = useCallback(
    async () => {
      if (!livekit_url || !roomContext?.name) return

      const payload = {
        room: roomContext.name,
        participantIdentities: [participant.identity],
        grantAccess: !canPublishMicrophone,
        updateRoomMetadata: false,
      }

      try {
        const response = await fetch(
          `${window.origin}/api/block_mute_participants?livekit_url=${livekit_url}`,
          {
            method: 'POST',
            body: JSON.stringify(payload),
            headers: {
              authorization: accessToken || '',
              'Content-Type': 'application/json',
            },
          },
        )

        if (!response.ok) {
          console.error('Failed to toggle microphone block:', await response.text())
        }
      } catch (error) {
        console.error('Error toggling microphone block:', error)
      }
    },
    [accessToken, livekit_url, roomContext?.name, participant.identity, canPublishMicrophone],
  )

  const toggleScreenShareBlock = useCallback(
    async () => {
      if (!livekit_url || !roomContext?.name) return

      const payload = {
        room: roomContext.name,
        participantIdentities: [participant.identity],
        grantAccess: !canPublishScreenShare,
        updateRoomMetadata: false,
        trackType: 'screenShare',
      }

      try {
        const response = await fetch(
          `${window.origin}/api/block_mute_participants?livekit_url=${livekit_url}`,
          {
            method: 'POST',
            body: JSON.stringify(payload),
            headers: {
              authorization: accessToken || '',
              'Content-Type': 'application/json',
            },
          },
        )

        if (!response.ok) {
          console.error('Failed to toggle screen share block:', await response.text())
        }
      } catch (error) {
        console.error('Error toggling screen share block:', error)
      }
    },
    [accessToken, livekit_url, roomContext?.name, participant.identity, canPublishScreenShare],
  )

  //Перемещает участника в другую группу или основную комнату
  //Вызывает API эндпоинт для изменения атрибута groupId участника
  const moveParticipantToGroup = useCallback(
    async (targetGroupId: string | null) => {
      // Проверки безопасности: не выполняем действие, если нет прав или группа не изменилась
      if (!hasGroupTargets) {
        console.log('[moveParticipantToGroup] Blocked: no group targets')
        return
      }
      if (targetGroupId === currentGroupId) {
        console.log('[moveParticipantToGroup] Blocked: same group')
        return
      }
      if (!livekit_url || !roomContext?.name) {
        console.log('[moveParticipantToGroup] Blocked: missing livekit_url or room name')
        return
      }

      // Устанавливаем состояние загрузки для блокировки повторных запросов
      setIsUpdatingGroup(true)
      console.log('[moveParticipantToGroup] Sending request...')

      try {
        // Отправляем запрос для изменения группы участника
        const response = await fetch(
          `${window.origin}/api/move_participant?livekit_url=${livekit_url}`,
          {
            method: 'POST',
            headers: {
              authorization: accessToken || '',
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              roomName: roomContext.name,
              participantIdentity: participant.identity,
              groupId: targetGroupId,
            }),
          },
        )

        if (!response.ok) {
          const errorText = await response.text()
          console.error('[moveParticipantToGroup] Failed:', errorText)
        } else {
          const result = await response.json()
          console.log('[moveParticipantToGroup] Success:', result)
        }
        // При успешном ответе LiveKit автоматически отправит событие ParticipantAttributesChanged
        // которое обработается хуком useGroupTrackSubscriptions для обновления подписок
      } catch (error) {
        console.error('[moveParticipantToGroup] Error:', error)
      } finally {
        // Снимаем блокировку независимо от результата
        setIsUpdatingGroup(false)
        console.log('[moveParticipantToGroup] Complete')
      }
    },
    [
      accessToken,
      currentGroupId,
      hasGroupTargets,
      livekit_url,
      participant.identity,
      roomContext?.name,
    ],
  )




  // Обработчик клика по пункту меню для перемещения участника
  // Используется для перемещения через контекстное меню (три точки)

  const handleMoveViaMenu = useCallback(
    (event: ClickEvent, targetGroupId: string | null) => {
      // Предотвращаем всплытие события, чтобы не сработали другие обработчики
      event.stopPropagation = true
      moveParticipantToGroup(targetGroupId).catch((error) => {
      console.error('[handleMoveViaMenu] Unhandled error:', error)
      })
    },
    [moveParticipantToGroup],
  )

  const menuButton = useMemo(
    () => (
      <button id="removeUser" aria-label="show menu" className="button-sm">
        <MoreIcon fill={text_primary_color} />
      </button>
    ),
    [text_primary_color],
  )

  const isHandRaised = participantsReactions[participant.identity]?.isHandRaised

  return (
    <>
      <div className="lk-participant-item">
        <div className="lk-participant-item-avatar-wrapper">
          <div
            className="lk-participant-item-avatar"
            style={{ background: `linear-gradient(135deg, ${avatarColors[0]} 0%, ${avatarColors[1]} 100%)` }}
          >
            {participant.name
              ?.split(' ')
              .map((item) => item.slice(0, 1).toUpperCase())
              .join('')
              .slice(0, 2)}
          </div>
          {isHandRaised && <div className="lk-participant-item-hand-badge">🖐️</div>}
        </div>

        <div className="lk-participant-item-info" style={{ color: `${text_primary_color}` }}>
          <div className="lk-participant-item-name">{participant.name}</div>
        </div>

        {shouldShowMenu && (
          <Menu
            menuButton={menuButton}
            transition
            direction="bottom"
            align="start"
            viewScroll="auto"
            position="anchor"
            portal
          >
            {canRemoveParticipant && (
              <MenuItem onClick={removeFromRoom} style={{ color: `${text_primary_color}` }}>
                <DeleteIcon fill={text_primary_color} />
                {t('removeParticipant')}
              </MenuItem>
            )}
            {hasGroupTargets &&
              normalizedGroupOptions.map((option) => {
                const optionGroupId = option.id ?? null
                const isCurrent = currentGroupId === optionGroupId
                const label =
                  optionGroupId === null
                    ? t('moveToMainRoom')
                    : t('moveToGroup', { groupName: option.name })
                return (
                  <MenuItem
                    key={`group-action-${option.id ?? 'main'}`}
                    style={{ color: `${text_primary_color}` }}
                    disabled={isUpdatingGroup || isCurrent} // Блокируем текущую группу и во время обновления
                    onClick={(event) => handleMoveViaMenu(event, optionGroupId)}
                  >
                    {label}
                  </MenuItem>
                )
              })}
          </Menu>
        )}

        {!participant.isLocal && (isAdmin || localParticipantIsCurator) && (
          <button className="button-sm" onClick={toggleMicrophoneBlock}>
            {canPublishMicrophone ? (
              <VoiceOn width={20} height={20} fill={text_primary_color} />
            ) : (
              <VoiceOff width={20} height={20} fill={text_primary_color} />
            )}
          </button>
        )}

        {!participant.isLocal && (isAdmin || localParticipantIsCurator) && (
          <button className="button-sm" onClick={toggleScreenShareBlock}>
            {canPublishScreenShare ? (
              <ScreenShareIcon fill={text_primary_color} />
            ) : (
              <ScreenShareStopIcon fill={text_primary_color} />
            )}
          </button>
        )}

        {!participant.isLocal && isAdmin && (
          <>
            <button className="button-sm" onClick={() => togglePermissions(Track.Source.Microphone)}>
              {!isAudioMuted ? (
                <MicIcon fill={text_primary_color} />
              ) : (
                <MicDisabledIcon fill={text_primary_color} />
              )}
            </button>

            <button className="button-sm" onClick={() => togglePermissions(Track.Source.Camera)}>
              {!isVideoMuted ? (
                <CameraIcon fill={text_primary_color} />
              ) : (
                <CameraDisabledIcon fill={text_primary_color} />
              )}
            </button>
          </>
        )}
      </div>
    </>
  )
}
